"use client"
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useConfig } from './ConfigProvider';
import { useAuth } from './AuthProvider';
import { horizontalAlignments, verticalAlignments } from './formrenderer/utils/EditorEnums';
import { evaluateTemplate, get, set } from './formrenderer/utils/Utils';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Control } from './formrenderer/ControlRenderer';

// Define the shape of your formData
interface FormData {
  // Define your formData properties here
}

export interface PageType {
  PKey: number;
  Name: string;
  Description: string | null;
  Alias: string;
  Layout: Control;
}

// Define the shape of props for FormDataProvider
interface FormDataProviderProps {
  children: React.ReactNode;
}

export type FormModeType = 'edit' | 'view' | 'design' | 'label' | 'hidden';

// Define the shape of your context
interface FormDataContextType {
  formKey: number;
  formMode: FormModeType;
  formData: FormData | null;
  setFormData: (formData: FormData | null) => void;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  setError: (error: string) => void;
  //refreshCount: number;
  setFormDataInfo: (formKey: number, formMode: FormModeType) => void;
  setFormDataElement: (dataKey: string, newValue: any) => void;
  navigatePage: (path: string) => void;
  getPage: (alias: string) => PageType | undefined;
  refreshForm: () => void;
  fetchFormDataElement: (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => void;
  fetchLookupData: (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => void;
  prepareLookupData: (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => void;
}

// Create a context for FormData
const FormDataContext = createContext<FormDataContextType>({
  formKey: 0,
  formMode: 'edit',
  formData: null,
  setFormData: () => { },
  isLoading: false,
  isError: false,
  errorMessage: null,
  setError: () => { },
  //refreshCount: 0,
  setFormDataInfo: () => { },
  setFormDataElement: () => { },
  navigatePage: () => { },
  getPage: () => { return undefined; },
  refreshForm: () => { },
  fetchFormDataElement: () => { },
  fetchLookupData: () => { },
  prepareLookupData: () => { }
});

// Custom hook to access FormData context
export const useFormData = () => useContext(FormDataContext);

// FormDataProvider component
const FormDataProvider: React.FC<FormDataProviderProps> = ({ children }) => {
  const { apiUrl } = useConfig(); // Get the API root URL from config
  const { token } = useAuth(); // Get the bearer token
  // State to store formKey
  const [formKey, setFormKey] = useState<number>(0);
  // State to store formMode
  const [formMode, setFormMode] = useState<FormModeType>('edit');
  // State to store formData
  const [formData, setFormData] = useState<FormData | null>({});
  // State to track loading state
  const [isLoading, setIsLoading] = useState(true);
  // State to track error state
  const [isError, setIsError] = useState(false);
  // State to store error message
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // used to trigger a fetch even if the form is the same
  const [forceFetch, setForceFetch] = useState(false);
  // Counter to trigger a refresh if needed
  //const [refreshCount, setRefreshCount] = useState<number>(0);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const router = useRouter();

  // Helper function to set properties with a specific prefix to null
  const setPrefixedPropsToNull = (obj: any, prefix: string) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => {
        if (key.startsWith(prefix)) {
          return [key, null]; // Set to null if key starts with the prefix
        }
        return [key, value]; // Otherwise, keep the original key and value
      })
    );
  };

  useEffect(() => {
    // clear validation errors when navigation occurred
    //setFormDataElement("validationResult_errors", null);
    setFormDataElement("validationResult_resultMessage", null);
    // clear prefixed properties when navigation occurred
    setFormData(prevFormData => {
      return setPrefixedPropsToNull(prevFormData, "_");
    });
  }, [pathname, searchParams]);

  // useEffect to fetch formData when formKey or formMode changes
  useEffect(() => {
    // Function to fetch formData based on formKey and formMode
    const fetchFormData = async (key: number, mode: FormModeType) => {
      //console.log("fetchFormData");
      setIsLoading(true);
      setIsError(false); // Reset error state
      setErrorMessage(null); // Reset error message
      try {
        const response = await fetch(`${apiUrl}/Form/${mode}/${key}`, {
          method: 'GET',
          mode: 'cors', // Allow cross-origin requests
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            // Add any additional headers if needed
          },
        }); // Adjust your API endpoint accordingly
        if (response.ok) {
          const data = await response.json();
          setFormDataElements(data);
        } else {
          setIsError(true); // Set error state if response is not ok
          const errorText = await response.text();
          setErrorMessage(`Failed to fetch form data: ${response.status} ${errorText}`); // Set error message
        }
      } catch (error) {
        console.error('Error fetching formData:', error);
        setIsError(true); // Set error state
        setErrorMessage('Error fetching form data'); // Set error message
      } finally {
        setIsLoading(false); // Set loading state to false when fetching is complete
      }
    };

    if (apiUrl && token && formKey !== 0) {
      fetchFormData(formKey, formMode);
    } else {
      setIsLoading(false); // Stop loading if apiUrl or formKey is invalid
    }
  }, [apiUrl, token, formKey, formMode, forceFetch]); // Fetch when apiUrl, formKey, or formMode changes

  // useEffect to fetch initial formData elements when component mounts
  useEffect(() => {
    const fetchInitialFormData = async () => {
      setIsLoading(true);
      setIsError(false); // Reset error state
      setErrorMessage(null); // Reset error message
      try {
        // Fetch initial form data elements
        const response = await fetch(`${apiUrl}/Form/initialFormData`, {
          method: 'GET',
          mode: 'cors', // Allow cross-origin requests
          headers: {
            'Content-Type': 'application/json'
            // Add any additional headers if needed
          },
        });
        if (response.ok) {
          const initialData = await response.json();
          // Set up local formData elements
          initialData.horizontalAlignments = horizontalAlignments;
          initialData.verticalAlignments = verticalAlignments;
          // Update formData state with initial data
          setFormDataElements(initialData);
        } else {
          setIsError(true); // Set error state if response is not ok
          const errorText = await response.text();
          setErrorMessage(`Failed to fetch initial form data: ${response.status} ${errorText}`); // Set error message
        }
      } catch (error) {
        console.error('Error fetching initial formData:', error);
        setIsError(true); // Set error state
        setErrorMessage('Error fetching initial form data'); // Set error message
      } finally {
        setIsLoading(false); // Set loading state to false when fetching is complete
      }
    };

    if (apiUrl) {
      fetchInitialFormData();
    } else {
      setIsLoading(false); // Stop loading if apiUrl is invalid
    }
  }, [apiUrl]); // Fetch when apiUrl changes

  // Function to set formKey and formMode
  const setFormDataInfo = (key: number, mode: FormModeType) => {
    setFormKey(key);
    setFormMode(mode);
    setIsError(false);
    setForceFetch(prev => !prev); // Toggle forceFetch to trigger the useEffect
  };

  const setError = (error: string) => {
    setIsError(!!error); // Reset error state
    setErrorMessage(error); // Reset error message
  };

  // Function to update formdata with a specific prop
  const setFormDataElement = (dataKey: string, newValue: any) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      [dataKey]: newValue
    }));
  };

  // Function to update formdata with new properties
  const setFormDataElements = (newValue: any) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      ...newValue
    }));
  };

  const navigatePage = (path: string) => {
    let trimmedStr = path.replace(/^\/+/, ''); // Removes leading slashes
    // check if we have the page from the database
    const page = getPage(trimmedStr)
    if (page) {
      router.push(`/pages?page=${page.Alias}`);
    }
    else {
      router.push(path);
    }
  };

  const getPage = (alias: string): PageType | undefined => {
    // check if we have the page from the database
    return get(formData, "Pages", []).find((item: PageType) => item.Alias === alias);
  };

  // Function to refresh the form
  const refreshForm = () => {
    setFormData({
      ...formData
    });
    //setRefreshCount(refreshCount + 1);
  };

  const fetchFormDataElement = async (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => {
    setIsLoading(true);
    setIsError(false); // Reset error state
    setErrorMessage(null); // Reset error message

    const postData = postDataKey == "FormKey" ? { "FormKey": formKey } : get(formData, postDataKey, {});

    const currentKey = evaluateTemplate(dataKey, postData, formData);

    console.log(`fetchFormDataElement ${currentKey}`);

    try {
      const response = await fetch(`${apiUrl}/Form/item/${currentKey}`, {
        method: 'POST',
        mode: 'cors', // Allow cross-origin requests
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
          // Add any additional headers if needed
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        const data = await response.json();
        // Process response data as needed
        setFormDataElement(resultDataKey ?? currentKey, data);
      } else {
        setIsError(true);
        const errorText = await response.text();
        setErrorMessage(`Failed to fetch form data item: ${currentKey} ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching formDataItem:', error);
      setIsError(true);
      setErrorMessage('Error fetching form data item');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLookupData = async (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => {
    setIsLoading(true);
    setIsError(false); // Reset error state
    setErrorMessage(null); // Reset error message

    const postData = postDataKey == "FormKey" ? { "FormKey": formKey } : get(formData, postDataKey ?? "", {});

    const currentKey = evaluateTemplate(dataKey, postData, formData);

    console.log(`fetchLookupData ${currentKey}`);

    try {
      const response = await fetch(`${apiUrl}/Lookup/item/${currentKey}`, {
        method: 'POST',
        mode: 'cors', // Allow cross-origin requests
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
          // Add any additional headers if needed
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        const data = await response.json();
        // Process response data as needed
        setFormDataElement(resultDataKey ?? currentKey, data);
      } else {
        setIsError(true);
        const errorText = await response.text();
        setErrorMessage(`Failed to fetch lookup data item: ${currentKey} ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching LookupDataItem:', error);
      setIsError(true);
      setErrorMessage('Error fetching lookup data item');
    } finally {
      setIsLoading(false);
    }
  };

  const prepareLookupData = async (dataKey: string, postDataKey: string, resultDataKey: string | undefined) => {
    const postData = get(formData, postDataKey ?? "", {});

    const currentKey = evaluateTemplate(dataKey, postData, formData);

    var lookupData = get(formData, dataKey, undefined);

    if (!lookupData && apiUrl) {
      // set initial value
      console.log(`prepareLookupData ${currentKey}`);
      
      set(formData, dataKey, "Loading values ...");
      try {
        const response = await fetch(`${apiUrl}/Lookup/item/${currentKey}`, {
          method: 'POST',
          mode: 'cors', // Allow cross-origin requests
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            // Add any additional headers if needed
          },
          body: JSON.stringify(postData)
        });

        if (response.ok) {
          const data = await response.json();
          // Process response data as needed
          setFormDataElement(resultDataKey ?? currentKey, data);
        } else {
          const errorText = await response.text();
          console.error(`Failed to fetch lookup data item: ${currentKey} ${response.status} ${errorText}`);
          setFormDataElement(resultDataKey ?? currentKey, "Loading failed");
        }
      } catch (error) {
        console.error('Error fetching LookupDataItem:', error);
        setFormDataElement(resultDataKey ?? currentKey, "Loading failed");
      }
    }
  };

  // Define the value of the context
  const contextValue: FormDataContextType = {
    formKey,
    formMode,
    formData,
    setFormData,
    isLoading,
    isError,
    setError,
    errorMessage,
    //refreshCount,
    setFormDataInfo,
    setFormDataElement,
    refreshForm,
    fetchFormDataElement,
    navigatePage,
    getPage,
    fetchLookupData,
    prepareLookupData
  };

  return (
    <FormDataContext.Provider value={contextValue}>
      {children}
    </FormDataContext.Provider>
  );
};

export default FormDataProvider;
